"use client";

import * as React from "react";

import { cn } from "~/lib/utils";

interface TabsCtx {
  value: string;
  setValue: (value: string) => void;
}
const Ctx = React.createContext<TabsCtx | null>(null);

function useTabs(): TabsCtx {
  const ctx = React.useContext(Ctx);
  if (!ctx) throw new Error("Tabs parts must be used within <Tabs>");
  return ctx;
}

export function Tabs({
  defaultValue = "",
  value,
  onValueChange,
  className,
  children,
}: {
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  children: React.ReactNode;
}) {
  const [internal, setInternal] = React.useState(defaultValue);
  const isControlled = value !== undefined;
  const current = isControlled ? value : internal;
  const setValue = (v: string) => {
    if (!isControlled) setInternal(v);
    onValueChange?.(v);
  };
  return (
    <Ctx.Provider value={{ value: current, setValue }}>
      <div className={className}>{children}</div>
    </Ctx.Provider>
  );
}

export function TabsList({ className, children }: React.ComponentProps<"div">) {
  return (
    <div
      role="tablist"
      className={cn(
        "inline-flex items-center gap-1 border-b border-border",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Roving-tabindex keyboard nav within the tablist (WAI-ARIA tabs pattern). */
function onTabKeyDown(
  e: React.KeyboardEvent<HTMLButtonElement>,
  select: (value: string) => void,
) {
  const keys = ["ArrowRight", "ArrowLeft", "Home", "End"];
  if (!keys.includes(e.key)) return;
  const tabs = Array.from(
    e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
      '[role="tab"]',
    ) ?? [],
  );
  const i = tabs.indexOf(e.currentTarget);
  if (i === -1) return;
  e.preventDefault();
  const next =
    e.key === "Home"
      ? tabs[0]
      : e.key === "End"
        ? tabs[tabs.length - 1]
        : e.key === "ArrowRight"
          ? tabs[(i + 1) % tabs.length]
          : tabs[(i - 1 + tabs.length) % tabs.length];
  if (!next) return;
  next.focus();
  const value = next.dataset.value;
  if (value !== undefined) select(value);
}

export function TabsTrigger({
  value,
  className,
  children,
}: {
  value: string;
  className?: string;
  children: React.ReactNode;
}) {
  const { value: current, setValue } = useTabs();
  const active = current === value;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      // Roving tabindex: only the active tab is in the tab sequence; arrows
      // move between the rest (WAI-ARIA tabs pattern).
      tabIndex={active ? 0 : -1}
      data-value={value}
      onClick={() => setValue(value)}
      onKeyDown={(e) => onTabKeyDown(e, setValue)}
      className={cn(
        "relative -mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]",
        active
          ? "border-primary text-fg"
          : "border-transparent text-fg-muted hover:text-fg",
        className,
      )}
    >
      {children}
    </button>
  );
}

export function TabsContent({
  value,
  className,
  children,
  forceMount = false,
}: {
  value: string;
  className?: string;
  children: React.ReactNode;
  /** Keep staged forms and their save controllers alive across tab changes. */
  forceMount?: boolean;
}) {
  const { value: current } = useTabs();
  const active = current === value;
  if (!active && !forceMount) return null;
  return (
    <div role="tabpanel" hidden={!active} style={active ? undefined : { display: "none" }} className={cn("pt-4", className)}>
      {children}
    </div>
  );
}
